import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import {
  Loader2, ArrowLeft, FileText, Download, CheckCircle2, Save, RefreshCw,
  Plus, Trash2, AlertCircle, ClipboardCheck, Camera, X, ImagePlus,
  Wrench, ExternalLink, History, Mail,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = ["SCHEDULED", "IN_PROGRESS", "AWAITING_REVIEW", "COMPLETED", "ACTION_REQUIRED"];
const STATUS_LABELS = {
  SCHEDULED: "Scheduled",
  IN_PROGRESS: "Inspection In Progress",
  AWAITING_REVIEW: "Awaiting Review",
  COMPLETED: "Completed",
  ACTION_REQUIRED: "Action Required",
};
const PRIORITIES = [["HIGH", "High"], ["MEDIUM", "Medium"], ["LOW", "Low"]];
const TIMESCALES = [["IMMEDIATE", "Immediate"], ["3_MONTHS", "3 Months"], ["6_MONTHS", "6 Months"], ["12_MONTHS", "12 Months"]];
const RATING_BTNS = [
  { v: "GREEN", label: "GREEN", bg: "bg-[#064E2E]", text: "Good condition" },
  { v: "AMBER", label: "AMBER", bg: "bg-[#B88A2A]", text: "Monitor" },
  { v: "RED",   label: "RED",   bg: "bg-[#B91C1C]", text: "Action required" },
];

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function HealthCheckEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [check, setCheck] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCheck = useCallback(async () => {
    try {
      const { data } = await adminApi.get(`/health-checks/${id}`);
      setCheck(data);
      setLoading(false);
    } catch (err) {
      toast.error("Failed to load");
      navigate("/adminpanel/health-checks");
    }
  }, [id, navigate]);
  const load = fetchCheck;

  useEffect(() => {
    let active = true;
    (async () => { if (active) await fetchCheck(); })();
    return () => { active = false; };
  }, [fetchCheck]);

  if (loading || !check) {
    return <div className="text-[#475569] flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;
  }

  return (
    <div data-testid="health-check-editor">
      <div className="mb-5">
        <Link to="/adminpanel/health-checks" className="text-xs text-[#475569] hover:text-[#064E2E] inline-flex items-center gap-1">
          <ArrowLeft size={12}/> Back to Health Checks
        </Link>
      </div>

      <Header check={check} onChanged={load}/>

      <div className="grid lg:grid-cols-12 gap-5 mt-5">
        <div className="lg:col-span-8 space-y-4">
          <SummaryCard check={check} onChanged={load}/>
          {(check.sections || []).map((s) => (
            <SectionCard key={s.key} checkId={check.id} section={s} onChanged={load}/>
          ))}
        </div>
        <aside className="lg:col-span-4 space-y-4">
          <OverallTile check={check}/>
          <RecommendationsCard check={check} onChanged={load}/>
          <FollowOnJobsCard check={check}/>
          <PublishCard check={check} onChanged={load}/>
          <AuditTimelineCard checkId={check.id} refreshKey={check.updated_at}/>
        </aside>
      </div>
    </div>
  );
}

function Header({ check, onChanged }) {
  return (
    <div className="admin-page-header flex flex-wrap items-end justify-between gap-3 !mb-0">
      <div>
        <p className="eyebrow">Annual Health Check · {check.id.slice(0, 8).toUpperCase()}</p>
        <h1>{check.member_name}</h1>
        <p className="lead">{check.property_address || "—"}</p>
      </div>
      <div className="flex items-center gap-2">
        <a href={`${API_BASE}/api/admin/health-checks/${check.id}/pdf`} target="_blank" rel="noreferrer" className="btn-outline h-10 py-0 px-3 inline-flex items-center gap-1.5" data-testid="hc-pdf-btn">
          <Download size={14}/> PDF preview
        </a>
        <button onClick={onChanged} className="btn-outline h-10 py-0 px-3"><RefreshCw size={14}/></button>
      </div>
    </div>
  );
}

function SummaryCard({ check, onChanged }) {
  const [draft, setDraft] = useState({
    inspector_name: check.inspector_name || "",
    inspection_date: check.inspection_date || "",
    property_address: check.property_address || "",
    executive_summary: check.executive_summary || "",
    status: check.status,
  });
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await adminApi.patch(`/health-checks/${check.id}`, draft);
      toast.success("Saved");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  }

  return (
    <section className="admin-panel overflow-hidden" data-testid="hc-summary">
      <header className="px-5 py-4 border-b border-[#E2DCD0] bg-gradient-to-b from-[#F7F4ED]/80 to-white flex items-center gap-3">
        <span className="w-2 h-8 rounded-r bg-[#064E2E]"/>
        <div>
          <h3 className="font-display text-lg text-[#071A33]">Inspection details</h3>
          <p className="text-[11px] text-[#475569]">Inspector, date, status and executive summary.</p>
        </div>
      </header>
      <div className="p-5 grid sm:grid-cols-2 gap-4">
        <Field label="Inspector name">
          <input className="field-input" value={draft.inspector_name} onChange={(e) => setDraft({ ...draft, inspector_name: e.target.value })} data-testid="hc-summary-inspector"/>
        </Field>
        <Field label="Inspection date">
          <input type="date" className="field-input" value={(draft.inspection_date || "").slice(0,10)} onChange={(e) => setDraft({ ...draft, inspection_date: e.target.value })} data-testid="hc-summary-date"/>
        </Field>
        <Field label="Property address" full>
          <input className="field-input" value={draft.property_address} onChange={(e) => setDraft({ ...draft, property_address: e.target.value })} data-testid="hc-summary-address"/>
        </Field>
        <Field label="Status">
          <select className="field-input" value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} data-testid="hc-summary-status">
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </Field>
        <Field label="Executive summary" full>
          <textarea rows={3} className="field-input py-2" value={draft.executive_summary} onChange={(e) => setDraft({ ...draft, executive_summary: e.target.value })} data-testid="hc-summary-exec" placeholder="One paragraph: overall condition, key concerns, what the member needs to know first."/>
        </Field>
        <div className="sm:col-span-2 flex justify-end">
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="hc-summary-save">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Save details
          </button>
        </div>
      </div>
    </section>
  );
}

function SectionCard({ checkId, section, onChanged }) {
  const [notes, setNotes] = useState(section.section_notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  async function setRating(itemKey, rating) {
    setBusyKey(itemKey);
    try {
      const item = section.items.find((i) => i.key === itemKey);
      await adminApi.patch(`/health-checks/${checkId}/item`, {
        section_key: section.key, item_key: itemKey, rating, notes: item?.notes || "",
      });
      onChanged?.();
    } catch (e) { toast.error("Save failed"); }
    finally { setBusyKey(null); }
  }

  async function setItemNotes(itemKey, value) {
    try {
      const item = section.items.find((i) => i.key === itemKey);
      await adminApi.patch(`/health-checks/${checkId}/item`, {
        section_key: section.key, item_key: itemKey, rating: item?.rating ?? null, notes: value,
      });
      onChanged?.();
    } catch (e) { toast.error("Save failed"); }
  }

  async function saveSectionNotes() {
    setSavingNotes(true);
    try {
      await adminApi.patch(`/health-checks/${checkId}/section-notes`, { section_key: section.key, section_notes: notes });
      toast.success("Section notes saved");
      onChanged?.();
    } catch (e) { toast.error("Save failed"); }
    finally { setSavingNotes(false); }
  }

  return (
    <section className="admin-panel overflow-hidden" data-testid={`hc-section-${section.key}`}>
      <header className="px-5 py-4 border-b border-[#E2DCD0] bg-gradient-to-b from-[#F7F4ED]/80 to-white">
        <h3 className="font-display text-lg text-[#071A33] tracking-tight">{section.title}</h3>
        {section.disclaimer && <p className="text-[11px] italic text-[#B88A2A] mt-1">⚠ {section.disclaimer}</p>}
      </header>
      <div className="divide-y divide-[#E2DCD0]">
        {section.items.map((it) => (
          <div key={it.key} className="px-5 py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="font-medium text-[#071A33]">{it.label}</p>
              <div className="flex items-center gap-1.5" data-testid={`hc-rating-${section.key}-${it.key}`}>
                {RATING_BTNS.map((b) => (
                  <button
                    key={b.v}
                    onClick={() => setRating(it.key, it.rating === b.v ? null : b.v)}
                    disabled={busyKey === it.key}
                    className={`px-3 h-9 text-xs font-semibold rounded-md border transition ${it.rating === b.v ? `${b.bg} text-white border-transparent shadow-sm` : "bg-white text-[#475569] border-[#E2DCD0] hover:border-[#94A3B8]"}`}
                    title={b.text}
                    data-testid={`hc-rate-${section.key}-${it.key}-${b.v.toLowerCase()}`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>
            <textarea
              rows={2}
              className="field-input mt-3 py-2 text-sm"
              placeholder="Inspector notes for this item (optional)"
              defaultValue={it.notes || ""}
              onBlur={(e) => { if (e.target.value !== (it.notes || "")) setItemNotes(it.key, e.target.value); }}
              data-testid={`hc-notes-${section.key}-${it.key}`}
            />
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <PhotoStrip
                photos={it.photos || []}
                checkId={checkId}
                uploadUrl={`/health-checks/${checkId}/sections/${section.key}/items/${it.key}/photos`}
                onChanged={onChanged}
                testid={`hc-photos-${section.key}-${it.key}`}
              />
              {(it.rating === "RED" || it.rating === "AMBER") && (
                <CreateJobButton checkId={checkId} section_key={section.key} item_key={it.key} label={it.label} onChanged={onChanged}/>
              )}
            </div>
          </div>
        ))}
      </div>
      <footer className="px-5 py-4 bg-[#F7F4ED]/50 border-t border-[#E2DCD0]">
        <label className="field-label">Overall section notes</label>
        <textarea
          rows={2}
          className="field-input py-2 text-sm"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="A short summary covering the whole section (optional)"
          data-testid={`hc-section-notes-${section.key}`}
        />
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <PhotoStrip
            photos={section.photos || []}
            checkId={checkId}
            uploadUrl={`/health-checks/${checkId}/sections/${section.key}/photos`}
            onChanged={onChanged}
            testid={`hc-section-photos-${section.key}`}
          />
          <button onClick={saveSectionNotes} disabled={savingNotes} className="text-xs text-[#064E2E] hover:underline inline-flex items-center gap-1" data-testid={`hc-section-notes-save-${section.key}`}>
            {savingNotes ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Save section notes
          </button>
        </div>
      </footer>
    </section>
  );
}

function OverallTile({ check }) {
  const r = check.overall_rating;
  const map = {
    GREEN: { bg: "bg-[#064E2E]", text: "Good Condition" },
    AMBER: { bg: "bg-[#B88A2A]", text: "Monitor" },
    RED:   { bg: "bg-[#B91C1C]", text: "Action Required" },
  };
  const t = map[r];
  const counts = (check.sections || []).reduce((acc, s) => {
    for (const it of s.items || []) {
      if (it.rating === "GREEN") acc.green++;
      else if (it.rating === "AMBER") acc.amber++;
      else if (it.rating === "RED") acc.red++;
      else acc.unrated++;
    }
    return acc;
  }, { green: 0, amber: 0, red: 0, unrated: 0 });

  return (
    <section className={`rounded-xl overflow-hidden border ${t ? `${t.bg} text-white border-transparent` : "bg-white text-[#475569] border-[#E2DCD0]"}`} data-testid="hc-overall-tile">
      <div className="p-5">
        <p className={`text-[10px] tracking-[0.22em] uppercase font-semibold ${t ? "text-white/70" : "text-[#94A3B8]"}`}>Overall Rating</p>
        <p className="font-display text-3xl mt-1">{r || "Pending"}</p>
        <p className={`text-sm mt-1 ${t ? "text-white/85" : "text-[#475569]"}`}>{t?.text || "No items rated yet"}</p>
      </div>
      <div className={`grid grid-cols-3 text-center border-t ${t ? "border-white/15" : "border-[#E2DCD0]"}`}>
        <div className="py-3"><p className="font-display text-2xl">{counts.green}</p><p className="text-[10px] tracking-[0.18em] uppercase">Green</p></div>
        <div className={`py-3 border-x ${t ? "border-white/15" : "border-[#E2DCD0]"}`}><p className="font-display text-2xl">{counts.amber}</p><p className="text-[10px] tracking-[0.18em] uppercase">Amber</p></div>
        <div className="py-3"><p className="font-display text-2xl">{counts.red}</p><p className="text-[10px] tracking-[0.18em] uppercase">Red</p></div>
      </div>
    </section>
  );
}

function RecommendationsCard({ check, onChanged }) {
  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState({ priority: "MEDIUM", timescale: "3_MONTHS", text: "" });
  const [busy, setBusy] = useState(false);

  async function addRec() {
    if (!draft.text.trim()) { toast.error("Recommendation text is required"); return; }
    setBusy(true);
    try {
      await adminApi.post(`/health-checks/${check.id}/recommendations`, draft);
      setDraft({ priority: "MEDIUM", timescale: "3_MONTHS", text: "" });
      setShowAdd(false);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  async function deleteRec(rid) {
    if (!confirm("Remove this recommendation?")) return;
    try {
      await adminApi.delete(`/health-checks/${check.id}/recommendations/${rid}`);
      onChanged?.();
    } catch (e) { toast.error("Delete failed"); }
  }

  const priColor = { HIGH: "text-[#B91C1C]", MEDIUM: "text-[#B88A2A]", LOW: "text-[#0F766E]" };
  const tsLabel = Object.fromEntries(TIMESCALES);

  return (
    <section className="admin-panel" data-testid="hc-recommendations">
      <header className="px-5 py-4 border-b border-[#E2DCD0] flex items-center justify-between">
        <h3 className="font-display text-lg text-[#071A33]">Recommendations</h3>
        <button onClick={() => setShowAdd((v) => !v)} className="text-xs text-[#064E2E] hover:underline inline-flex items-center gap-1" data-testid="hc-rec-add-toggle">
          <Plus size={12}/> Add
        </button>
      </header>
      {showAdd && (
        <div className="p-4 border-b border-[#E2DCD0] bg-[#F7F4ED]/50 space-y-3" data-testid="hc-rec-add-form">
          <textarea rows={2} className="field-input py-2 text-sm" placeholder="What needs doing?" value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} data-testid="hc-rec-text"/>
          <div className="grid grid-cols-2 gap-2">
            <select className="field-input text-sm" value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: e.target.value })} data-testid="hc-rec-priority">
              {PRIORITIES.map(([v, l]) => <option key={v} value={v}>Priority: {l}</option>)}
            </select>
            <select className="field-input text-sm" value={draft.timescale} onChange={(e) => setDraft({ ...draft, timescale: e.target.value })} data-testid="hc-rec-timescale">
              {TIMESCALES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <button onClick={addRec} disabled={busy} className="btn-primary w-full" data-testid="hc-rec-submit">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>} Add recommendation
          </button>
        </div>
      )}
      <ul className="divide-y divide-[#E2DCD0]">
        {(check.recommendations || []).length === 0 && (
          <li className="px-5 py-6 text-xs text-[#475569] italic text-center">No recommendations yet.</li>
        )}
        {(check.recommendations || []).map((r) => (
          <li key={r.id} className="px-5 py-3 flex items-start gap-3" data-testid={`hc-rec-${r.id}`}>
            <div className="grow">
              <p className="text-[10px] tracking-[0.18em] uppercase font-semibold flex items-center gap-2">
                <span className={priColor[r.priority]}>{r.priority}</span>
                <span className="text-[#94A3B8]">·</span>
                <span className="text-[#475569]">{tsLabel[r.timescale] || r.timescale}</span>
              </p>
              <p className="text-sm text-[#071A33] mt-0.5 leading-snug">{r.text}</p>
            </div>
            <button onClick={() => deleteRec(r.id)} className="text-[#94A3B8] hover:text-[#B91C1C]" data-testid={`hc-rec-delete-${r.id}`}>
              <Trash2 size={13}/>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PublishCard({ check, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [resending, setResending] = useState(false);

  async function publish() {
    if (check.status === "COMPLETED") { toast.info("Already published"); return; }
    setBusy(true);
    try {
      await adminApi.patch(`/health-checks/${check.id}`, { status: "COMPLETED" }, {
        params: sendEmail ? { send_email_to_member: true } : {},
      });
      toast.success(sendEmail ? "Completed and emailed to member" : "Health Check completed");
      onChanged?.();
    } catch (e) { toast.error("Publish failed"); }
    finally { setBusy(false); }
  }

  async function resendEmail() {
    setResending(true);
    try {
      const { data } = await adminApi.post(`/health-checks/${check.id}/send-report`);
      if (data?.ok) {
        toast.success("Report emailed to member");
        onChanged?.();
      } else {
        toast.error(data?.error || "Email failed");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Email failed"); }
    finally { setResending(false); }
  }

  if (check.status === "COMPLETED") {
    const sent = !!check.email_sent_at;
    return (
      <section className="rounded-xl border border-[#064E2E]/30 bg-[#064E2E]/5 p-5" data-testid="hc-publish-done">
        <div className="flex items-start gap-3">
          <CheckCircle2 size={18} className="text-[#064E2E] mt-0.5 shrink-0"/>
          <div className="flex-1">
            <p className="font-display text-lg text-[#064E2E]">Completed</p>
            <p className="text-xs text-[#475569] mt-1 leading-relaxed">
              {sent
                ? `Emailed to ${check.member_email || "member"} on ${(check.email_sent_at || "").slice(0,10)}. Next due ${(check.next_due_at || "").slice(0,10) || "in 12 months"}.`
                : `Not yet emailed. Next due ${(check.next_due_at || "").slice(0,10) || "in 12 months"}.`}
            </p>
            <button
              onClick={resendEmail}
              disabled={resending}
              className="mt-3 text-xs text-[#064E2E] hover:underline inline-flex items-center gap-1"
              data-testid="hc-resend-email-btn"
            >
              {resending ? <Loader2 size={12} className="animate-spin"/> : <Mail size={12}/>}
              {sent ? "Resend report email" : "Send report email now"}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="admin-panel p-5" data-testid="hc-publish">
      <h3 className="font-display text-lg text-[#071A33]">Mark as Completed</h3>
      <p className="text-xs text-[#475569] mt-1 leading-relaxed">
        Locks the inspection, sets the overall rating, and schedules the next due date 12 months out.
      </p>
      <label className="mt-4 flex items-start gap-2 text-xs text-[#334155] cursor-pointer">
        <input
          type="checkbox"
          checked={sendEmail}
          onChange={(e) => setSendEmail(e.target.checked)}
          className="mt-0.5 w-3.5 h-3.5 accent-[#064E2E]"
          data-testid="hc-publish-email-checkbox"
        />
        <span>
          Email the PDF report link to the member on completion
          {check.member_email ? <span className="block text-[10px] text-[#475569] mt-0.5">→ {check.member_email}</span> : null}
        </span>
      </label>
      <button onClick={publish} disabled={busy} className="btn-primary mt-4 w-full" data-testid="hc-publish-btn">
        {busy ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>} Complete Inspection
      </button>
    </section>
  );
}

/* ============================================================
 * PhotoStrip — uploads + thumbnail grid for an item or section
 * ============================================================ */
function PhotoStrip({ photos, checkId, uploadUrl, onChanged, testid }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  async function handleFiles(files) {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await adminApi.post(uploadUrl, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(files.length === 1 ? "Photo uploaded" : `${files.length} photos uploaded`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function removePhoto(photoId) {
    if (!confirm("Remove this photo?")) return;
    try {
      await adminApi.delete(`/health-checks/${checkId}/photos/${photoId}`);
      toast.success("Photo removed");
      onChanged?.();
    } catch (e) { toast.error("Remove failed"); }
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      {(photos || []).map((p) => (
        <div key={p.photo_id} className="relative group">
          <button
            type="button"
            onClick={() => setPreview(p)}
            className="block w-16 h-16 rounded-md overflow-hidden border border-[#E2DCD0] bg-[#F7F4ED]"
            data-testid={`${testid}-thumb-${p.photo_id}`}
          >
            <img src={p.url} alt={p.caption || "Inspection"} className="w-full h-full object-cover"/>
          </button>
          <button
            type="button"
            onClick={() => removePhoto(p.photo_id)}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#B91C1C] text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
            data-testid={`${testid}-remove-${p.photo_id}`}
            title="Remove"
          >
            <X size={11}/>
          </button>
        </div>
      ))}
      <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-[#475569] hover:text-[#064E2E] border border-dashed border-[#94A3B8] rounded-md px-2.5 h-9">
        {busy ? <Loader2 size={12} className="animate-spin"/> : <ImagePlus size={12}/>}
        {busy ? "Uploading…" : "Add photos"}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(Array.from(e.target.files || []))}
          data-testid={`${testid}-input`}
        />
      </label>
      {preview && (
        <div
          className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-6"
          onClick={() => setPreview(null)}
          data-testid={`${testid}-preview`}
        >
          <button className="absolute top-4 right-4 text-white" onClick={() => setPreview(null)}><X size={20}/></button>
          <img src={preview.url} alt={preview.caption || ""} className="max-w-full max-h-full rounded-lg shadow-2xl"/>
          {preview.caption && (
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-sm bg-black/60 px-3 py-1.5 rounded-md">{preview.caption}</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ============================================================
 * CreateJobButton — opens the modal to spin off a Support Case
 * ============================================================ */
function CreateJobButton({ checkId, section_key, item_key, label, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");

  async function submit() {
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/health-checks/${checkId}/create-follow-on-job`, {
        section_key, item_key, notes,
      });
      toast.success(`Follow-on job created (${data.case_reference})`);
      setOpen(false);
      setNotes("");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create job");
    } finally { setBusy(false); }
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-xs px-2.5 h-9 rounded-md bg-[#B91C1C]/10 text-[#B91C1C] hover:bg-[#B91C1C]/15 border border-[#B91C1C]/30 font-medium"
        data-testid={`hc-create-job-${section_key}-${item_key}`}
      >
        <Wrench size={12}/> Create follow-on job
      </button>
      {open && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid={`hc-create-job-dialog-${item_key}`}>
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)}/>
          <div className="relative bg-white rounded-xl border border-[#E2DCD0] shadow-2xl w-full max-w-md p-6">
            <h3 className="font-display text-xl text-[#071A33]">Create follow-on job</h3>
            <p className="text-xs text-[#475569] mt-1">
              Creates a new support case linked to this inspection item ({label}). Photos, ratings and notes will be attached automatically.
            </p>
            <label className="field-label mt-5">Additional context (optional)</label>
            <textarea
              rows={3}
              className="field-input py-2 text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything the contractor or scheduler needs to know."
              data-testid={`hc-create-job-notes-${item_key}`}
            />
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="text-sm px-4 py-2 text-[#475569] hover:bg-[#F7F4ED] rounded-md">Cancel</button>
              <button onClick={submit} disabled={busy} className="btn-primary" data-testid={`hc-create-job-submit-${item_key}`}>
                {busy ? <Loader2 size={14} className="animate-spin"/> : <Wrench size={14}/>} Create job
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ============================================================
 * FollowOnJobsCard — aside listing all generated support cases
 * ============================================================ */
function FollowOnJobsCard({ check }) {
  const jobs = check.follow_on_jobs || [];
  if (jobs.length === 0) {
    return (
      <section className="admin-panel p-5" data-testid="hc-followon-empty">
        <h3 className="font-display text-lg text-[#071A33] flex items-center gap-2">
          <Wrench size={14} className="text-[#475569]"/> Follow-on jobs
        </h3>
        <p className="text-xs text-[#475569] mt-2 italic">No follow-on jobs created yet. Rate an item AMBER or RED to generate one.</p>
      </section>
    );
  }
  const priColor = { HIGH: "bg-[#B91C1C] text-white", MEDIUM: "bg-[#B88A2A] text-white", LOW: "bg-[#0F766E] text-white" };
  return (
    <section className="admin-panel" data-testid="hc-followon-list">
      <header className="px-5 py-4 border-b border-[#E2DCD0] flex items-center justify-between">
        <h3 className="font-display text-lg text-[#071A33] flex items-center gap-2">
          <Wrench size={14} className="text-[#475569]"/> Follow-on jobs
        </h3>
        <span className="text-[10px] tracking-[0.18em] uppercase text-[#475569] font-semibold">{jobs.length}</span>
      </header>
      <ul className="divide-y divide-[#E2DCD0]">
        {jobs.map((j) => (
          <li key={j.support_case_id} className="px-5 py-3" data-testid={`hc-followon-${j.support_case_id}`}>
            <div className="flex items-start gap-2">
              <span className={`shrink-0 text-[9px] tracking-[0.15em] uppercase font-semibold px-1.5 py-0.5 rounded ${priColor[j.priority] || "bg-[#475569] text-white"}`}>
                {j.priority || "MED"}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#071A33] font-medium leading-snug">{j.title}</p>
                <p className="text-[10px] text-[#475569] mt-0.5">
                  <span className="font-mono">{j.case_reference}</span> · {(j.created_from || "").toLowerCase()} · {(j.created_at || "").slice(0,10)}
                </p>
              </div>
              <Link
                to={`/adminpanel/workspace/${j.support_case_id}`}
                className="text-[#064E2E] hover:text-[#0a5b55]"
                title="Open workspace"
                data-testid={`hc-followon-open-${j.support_case_id}`}
              >
                <ExternalLink size={13}/>
              </Link>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ============================================================
 * AuditTimelineCard — shows the activity stream for this check
 * ============================================================ */
function AuditTimelineCard({ checkId, refreshKey }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await adminApi.get(`/health-checks/${checkId}/events`);
      setEvents(data.items || []);
    } catch (e) { /* swallow */ }
    setLoading(false);
  }, [checkId]);
  const load = fetchEvents;

  useEffect(() => {
    let active = true;
    (async () => { if (active) await fetchEvents(); })();
    return () => { active = false; };
  }, [fetchEvents, refreshKey]);

  const tone = {
    COMPLETED: "bg-[#064E2E]",
    REPORT_SENT: "bg-[#0F766E]",
    REPORT_EMAIL_FAILED: "bg-[#B91C1C]",
    FOLLOW_ON_JOB_CREATED: "bg-[#B88A2A]",
    PHOTO_UPLOADED: "bg-[#475569]",
    PHOTO_DELETED: "bg-[#94A3B8]",
    STATUS_CHANGED: "bg-[#3B5BDB]",
    ITEM_RATED: "bg-[#0F766E]",
    RENEWAL_REMINDER_SENT: "bg-[#064E2E]",
    RENEWAL_REMINDER_FAILED: "bg-[#B91C1C]",
  };

  return (
    <section className="admin-panel" data-testid="hc-audit-card">
      <header className="px-5 py-4 border-b border-[#E2DCD0] flex items-center justify-between">
        <h3 className="font-display text-lg text-[#071A33] flex items-center gap-2">
          <History size={14} className="text-[#475569]"/> Activity timeline
        </h3>
        <button onClick={load} className="text-[#475569] hover:text-[#064E2E]" title="Refresh" data-testid="hc-audit-refresh">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""}/>
        </button>
      </header>
      <div className="px-5 py-4">
        {loading && events.length === 0 ? (
          <p className="text-xs text-[#475569] italic flex items-center gap-1.5"><Loader2 size={12} className="animate-spin"/> Loading…</p>
        ) : events.length === 0 ? (
          <p className="text-xs text-[#475569] italic">No activity recorded yet.</p>
        ) : (
          <ol className="relative border-l border-[#E2DCD0] ml-1.5 space-y-3 max-h-[420px] overflow-y-auto pr-1">
            {events.map((ev) => (
              <li key={ev.id || `${ev.event_type}-${ev.created_at}`} className="pl-4 relative" data-testid={`hc-audit-event-${ev.event_type}`}>
                <span className={`absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full ${tone[ev.event_type] || "bg-[#94A3B8]"}`}/>
                <p className="text-[11px] text-[#071A33] leading-snug">{ev.message}</p>
                <p className="text-[10px] text-[#475569] mt-0.5">
                  <span className="uppercase tracking-[0.12em] font-medium">{ev.event_type?.toLowerCase().replace(/_/g, " ")}</span>
                  {" · "}
                  {(ev.created_at || "").replace("T", " ").slice(0, 16)}
                </p>
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}
